import React, { useState, useEffect, useRef } from 'react'
import { Box, Text, IconButton, Spinner, Textarea, Stack, Badge, Button, SimpleModal, Link } from '@/components/lib'
import { FaTimes, FaPaperPlane, FaChevronLeft, FaPlus } from 'react-icons/fa'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useLocation } from 'react-router'
import useStore from '../../store'
import utils from '../../utils/utils'
import api from '../../api'
import scorchNeutral from '../../images/scorch/neutral.png'
import scorchHelpful from '../../images/scorch/helpful.png'
import AssistantChartBlock from './AssistantChartBlock'

const THREAD_KEY = (userId, trialId) => `assistant_thread_${userId}_${trialId}`

const mdComponents = {
  p: ({ children }) => <Text mb={2} fontSize='sm' lineHeight='tall'>{children}</Text>,
  strong: ({ children }) => <Text as='strong' fontWeight='semibold'>{children}</Text>,
  em: ({ children }) => <Text as='em' fontStyle='italic'>{children}</Text>,
  ul: ({ children }) => <Box as='ul' pl={4} mb={2} fontSize='sm'>{children}</Box>,
  ol: ({ children }) => <Box as='ol' pl={4} mb={2} fontSize='sm'>{children}</Box>,
  li: ({ children }) => <Box as='li' mb={1}>{children}</Box>,
  code: ({ children, className }) => {
    if (className === 'language-chart') {
      return <AssistantChartBlock>{children}</AssistantChartBlock>
    }
    return (
      <Box as='code' px={1} rounded='sm' bg='bg.muted' fontSize='xs' fontFamily='mono'>
        {children}
      </Box>
    )
  },
  pre: ({ children }) => {
    if (children?.props?.className === 'language-chart') {
      return <>{children}</>
    }
    return (
      <Box
        as='pre'
        p={2}
        mb={2}
        rounded='md'
        bg='bg.muted'
        fontSize='xs'
        fontFamily='mono'
        overflowX='auto'
        whiteSpace='pre-wrap'
      >
        {children}
      </Box>
    )
  },
  h1: ({ children }) => <Text fontWeight='bold' fontSize='md' mb={1}>{children}</Text>,
  h2: ({ children }) => <Text fontWeight='bold' fontSize='sm' mb={1}>{children}</Text>,
  h3: ({ children }) => <Text fontWeight='semibold' fontSize='sm' mb={1}>{children}</Text>,
  a: ({ href, children }) => <AppLink href={href}>{children}</AppLink>,
  hr: () => <Box borderTop='1px solid' borderColor='border.muted' my={2} />,
  table: ({ children }) => (
    <Box overflowX='auto' mb={2}>
      <Box as='table' width='100%' fontSize='xs' style={{ borderCollapse: 'collapse' }}>
        {children}
      </Box>
    </Box>
  ),
  thead: ({ children }) => <Box as='thead' bg='bg.muted'>{children}</Box>,
  tbody: ({ children }) => <Box as='tbody'>{children}</Box>,
  tr: ({ children }) => (
    <Box as='tr' borderBottom='1px solid' borderColor='border.muted'>{children}</Box>
  ),
  th: ({ children }) => (
    <Box as='th' px={2} py={1} fontWeight='semibold' textAlign='left'>{children}</Box>
  ),
  td: ({ children }) => (
    <Box as='td' px={2} py={1}>{children}</Box>
  )
}

function AppLink ({ href, children }) {
  const isInternal = href && href.startsWith('/')
  if (isInternal) {
    return <Link to={href}>{children}</Link>
  }
  return <Link href={href} target='_blank' rel='noopener noreferrer'>{children}</Link>
}

function Message ({ role, text }) {
  const isUser = role === 'user'
  return (
    <Box
      display='flex'
      justifyContent={isUser ? 'flex-end' : 'flex-start'}
      mb={2}
    >
      <Box
        maxW='85%'
        px={3}
        py={2}
        rounded='lg'
        bg={isUser ? 'primary.muted' : 'bg.subtle'}
        color={isUser ? 'primary.fg' : 'fg.default'}
        style={{ wordBreak: 'break-word' }}
      >
        {isUser
          ? <Text fontSize='sm' style={{ whiteSpace: 'pre-wrap' }}>{text}</Text>
          : <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{text}</ReactMarkdown>}
      </Box>
    </Box>
  )
}

function formatThreadDate (dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export default function Assistant () {
  const { team, trial, user } = useStore()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  // view: 'list' | 'chat'
  const [view, setView] = useState('chat')
  const [threads, setThreads] = useState([])
  const [threadsLoading, setThreadsLoading] = useState(false)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [threadId, setThreadId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [initialising, setInitialising] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const scrollRef = useRef(null)

  const shouldShow = (
    user && trial &&
    utils.hasFeature(team, 'aiAssistant') &&
    utils.canUseTrialAssistant(user, trial)
  )

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, open])

  // Reset when the trial changes while the component stays mounted
  useEffect(() => {
    setOpen(false)
    setView('chat')
    setMessages([])
    setThreadId(null)
    setThreads([])
    setInput('')
  }, [trial?._id])

  const loadThread = (id) => {
    setInitialising(true)
    setView('chat')
    api.assistant.getThread(id, data => {
      setThreadId(data._id)
      setMessages(data.messages || [])
      window.localStorage.setItem(THREAD_KEY(user._id, trial._id), data._id)
      setInitialising(false)
    }, () => {
      window.localStorage.removeItem(THREAD_KEY(user._id, trial._id))
      startNewThread()
    })
  }

  const startNewThread = () => {
    setInitialising(true)
    setView('chat')
    api.assistant.startThread(trial._id, data => {
      setThreadId(data._id)
      setMessages(data.messages || [])
      window.localStorage.setItem(THREAD_KEY(user._id, trial._id), data._id)
      setInitialising(false)
    }, () => {
      setInitialising(false)
    })
  }

  const openChat = () => {
    setOpen(true)
    if (threadId) return

    const storedThreadId = window.localStorage.getItem(THREAD_KEY(user._id, trial._id))
    if (storedThreadId) {
      loadThread(storedThreadId)
    } else {
      startNewThread()
    }
  }

  const openThreadList = () => {
    setView('list')
    setThreadsLoading(true)
    api.assistant.listThreads(trial._id, data => {
      setThreads(data.threads || [])
      setThreadsLoading(false)
    }, () => {
      setThreadsLoading(false)
    })
  }

  const confirmDelete = () => {
    if (!confirmDeleteId) return
    setDeleting(true)
    api.assistant.deleteThread(confirmDeleteId, () => {
      if (confirmDeleteId === threadId) {
        setThreadId(null)
        setMessages([])
        window.localStorage.removeItem(THREAD_KEY(user._id, trial._id))
        setView('list')
      }
      setThreads(prev => prev.filter(t => t._id !== confirmDeleteId))
      setConfirmDeleteId(null)
      setDeleting(false)
    }, () => {
      setConfirmDeleteId(null)
      setDeleting(false)
    })
  }

  const sendMessage = () => {
    const text = input.trim()
    if (!text || loading || !threadId) return
    setInput('')
    setMessages(prev => [...prev, { role: 'user', text }])
    setLoading(true)
    api.assistant.sendMessage(threadId, text, location.pathname, data => {
      setMessages(prev => [...prev, { role: 'assistant', text: data.reply }])
      setLoading(false)
    }, () => {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, something went wrong. Please try again.' }])
      setLoading(false)
    })
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  if (!shouldShow) return null

  return (
    <Box position='fixed' bottom={6} right={6} zIndex={1000}>
      {!open
        ? (
          <Box
            as='button'
            aria-label='Open AI assistant'
            onClick={openChat}
            w='60px'
            h='60px'
            rounded='full'
            shadow='lg'
            overflow='hidden'
            cursor='pointer'
            border='none'
            p='3px'
            bg='bg.accent'
            transition='transform 0.15s ease, box-shadow 0.15s ease'
            _hover={{ transform: 'scale(1.08)', shadow: '2xl' }}
          >
            <Box as='img' src={scorchNeutral} alt='Scorch' w='100%' h='100%' rounded='full' style={{ objectFit: 'cover' }} />
          </Box>
          )
        : (
          <Box
            w='320px'
            h='460px'
            bg='bg.surface'
            border='1px solid'
            borderColor='border.default'
            rounded='xl'
            shadow='xl'
            display='flex'
            flexDirection='column'
            overflow='hidden'
          >
            {/* Header */}
            <Box
              px={4}
              py={3}
              bg='bg.subtle'
              borderBottom='1px solid'
              borderColor='border.default'
              display='flex'
              alignItems='center'
              justifyContent='space-between'
              flexShrink={0}
            >
              <Box display='flex' alignItems='center' gap={2}>
                {view === 'chat' && (
                  <IconButton
                    aria-label='Back to conversations'
                    size='xs'
                    variant='ghost'
                    onClick={openThreadList}
                  >
                    <FaChevronLeft />
                  </IconButton>
                )}
                <Box as='img' src={scorchHelpful} alt='Scorch' w='24px' h='24px' rounded='full' style={{ objectFit: 'cover' }} />
                <Box>
                  <Text fontWeight='semibold' fontSize='sm' lineHeight='short'>Scorch</Text>
                  {trial?.name && (
                    <Text fontSize='xs' color='fg.muted' lineHeight='short'>{trial.name}</Text>
                  )}
                </Box>
              </Box>
              <IconButton
                aria-label='Close assistant'
                size='xs'
                variant='ghost'
                onClick={() => setOpen(false)}
              >
                <FaTimes />
              </IconButton>
            </Box>

            {/* Thread list view */}
            {view === 'list' && (
              <Box flex={1} overflowY='auto' display='flex' flexDirection='column'>
                <Box px={4} py={3} display='flex' justifyContent='center' borderBottom='1px solid' borderColor='border.default'>
                  <Button size='sm' colorPalette='primary' onClick={startNewThread}>
                    <FaPlus />
                    New conversation
                  </Button>
                </Box>
                {threadsLoading
                  ? (
                    <Box display='flex' justifyContent='center' alignItems='center' flex={1}>
                      <Spinner size='sm' />
                    </Box>
                    )
                  : threads.length === 0
                    ? (
                      <Box px={4} py={6}>
                        <Text fontSize='sm' color='fg.muted' textAlign='center'>No previous conversations</Text>
                      </Box>
                      )
                    : threads.map(t => (
                      <Box
                        key={t._id}
                        display='flex'
                        alignItems='center'
                        px={4}
                        py={3}
                        borderBottom='1px solid'
                        borderColor='border.muted'
                        bg={t._id === threadId ? 'bg.muted' : 'transparent'}
                        _hover={{ bg: 'bg.subtle' }}
                        transition='background 0.1s'
                        gap={2}
                      >
                        <Box
                          as='button'
                          flex={1}
                          display='flex'
                          flexDirection='column'
                          alignItems='flex-start'
                          cursor='pointer'
                          bg='transparent'
                          border='none'
                          textAlign='left'
                          minW={0}
                          onClick={() => loadThread(t._id)}
                        >
                          <Text fontSize='xs' fontWeight='medium' noOfLines={1} truncate>
                            {t.preview || <Badge>New thread</Badge>}
                          </Text>
                          <Text fontSize='xs' color='fg.muted' mt={0.5}>{formatThreadDate(t.createdAt)}</Text>
                        </Box>
                        <IconButton
                          aria-label='Delete conversation'
                          size='xs'
                          variant='ghost'
                          flexShrink={0}
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(t._id) }}
                        >
                          <FaTimes />
                        </IconButton>
                      </Box>
                    ))}
              </Box>
            )}

            <SimpleModal
              isOpen={!!confirmDeleteId}
              onClose={() => setConfirmDeleteId(null)}
              header='Delete conversation'
              body={<Text fontSize='sm'>Are you sure you want to delete this conversation? This cannot be undone.</Text>}
              footer={(
                <Box display='flex' gap={2} justifyContent='flex-end'>
                  <Button size='sm' variant='ghost' onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
                  <Button size='sm' colorPalette='red' loading={deleting} onClick={confirmDelete}>Delete</Button>
                </Box>
              )}
            />

            {/* Chat view */}
            {view === 'chat' && (
              <>
                <Box
                  ref={scrollRef}
                  flex={1}
                  overflowY='auto'
                  px={3}
                  py={3}
                >
                  {initialising
                    ? (
                      <Box display='flex' justifyContent='center' alignItems='center' h='100%'>
                        <Spinner size='sm' />
                      </Box>
                      )
                    : (
                      <>
                        {messages.length === 0 && (
                          <Stack alignItems='center' gap={3}>
                            <Box>
                              <img src={scorchHelpful} alt='Scorch' style={{ objectFit: 'cover', height: '100px' }} />
                            </Box>
                            <Text fontSize='sm' color='fg.muted' textAlign='center' mt={8}>
                              Hi! I'm Scorch, your Trialflare assistant. I can answer questions about your trial and help you manage it.
                            </Text>
                          </Stack>
                        )}
                        {messages.map((msg, i) => (
                          <Message key={i} role={msg.role} text={msg.text} />
                        ))}
                        {loading && (
                          <Box display='flex' justifyContent='flex-start' mb={2}>
                            <Box px={3} py={2} rounded='lg' bg='bg.subtle'>
                              <Spinner size='xs' />
                              <Text fontSize='xs' color='fg.muted'>Scorch is thinking...</Text>
                            </Box>
                          </Box>
                        )}
                      </>
                      )}
                </Box>

                <Box
                  px={2}
                  py={2}
                  borderTop='1px solid'
                  borderColor='border.default'
                  display='flex'
                  gap={2}
                  alignItems='flex-end'
                  flexShrink={0}
                >
                  <Textarea
                    flex={1}
                    size='sm'
                    rows={1}
                    placeholder='Ask something...'
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={loading || initialising || !threadId}
                    resize='none'
                  />
                  <IconButton
                    aria-label='Send message'
                    size='sm'
                    colorPalette='primary'
                    onClick={sendMessage}
                    disabled={!input.trim() || loading || initialising || !threadId}
                  >
                    <FaPaperPlane />
                  </IconButton>
                </Box>
                <Text fontSize='2xs' color='fg.subtle' textAlign='center' pb={1}>
                  Scorch can make mistakes. Verify important information.
                </Text>
              </>
            )}
          </Box>
          )}
    </Box>
  )
}
